import { customElement, html, classMap } from '@adobe/edex/ui/edex-lit';
import { EdexTagName } from '@adobe/edex/ui/shared/constants';

import { $t } from '@adobe/edex/ui/shared/config/i18n';
import '@spectrum-web-components/checkbox/sp-checkbox.js';
import '@spectrum-web-components/overlay/active-overlay.js';
import '@spectrum-web-components/overlay/overlay-trigger.js';
import '@adobe/edex/ui/components/resource/resource-builder/utils/form-error/form-error';
import { indexMetadataByID, indexMetadataByLabel } from '@adobe/edex/ui/utils/marshal';
import { BaseElement } from '@adobe/edex/ui/components/base.element';
import { TreeSelectorChangePayload } from '@adobe/edex/ui/types/payload';
import { TreeItem, TreeSelectorParams } from '@adobe/edex/ui/components/shared/tree-selector/tree-selector.types';
export * from './tree-selector.types';
import '@spectrum-web-components/tags/sp-tags.js';
import { debounce, isEmpty } from 'lodash-es';
import { Textfield } from '@spectrum-web-components/textfield';
import '@spectrum-web-components/menu/sp-menu.js';
import '@spectrum-web-components/popover/sp-popover.js';
import { ADOBE_EXPRESS, DEBOUNCE_TIME } from '../../search-page/constants';
import { INCLUDES_TEMPLATE, SHOW_ALL } from '@adobe/edex/ui/constants';

import(
  /* webpackChunkName: "component/item-selector-element-styles" */
  /* webpackMode: "lazy" */
  './item-selector.scss'
);

type TreeParams = TreeSelectorParams;

interface TreeItemParams extends TreeParams {
  item: TreeItem;
  index: number;
}

@customElement(EdexTagName.itemSelector)
export class ItemTreeSelector extends BaseElement {
  get useLightDom(): boolean {
    return true;
  }

  static get properties() {
    return {
      params: { type: Object },
      itemsById: { attribute: false },
      itemsByLabel: { attribute: false },
      defaultItems: { type: Object },
      filteredItems: { type: Object },
      selectedItems: { type: Object },
      showAll: { type: Boolean },
      deletables: { type: Object },
      isRender: { type: Boolean },
      showEditableTemplate: { type: Boolean },
    };
  }
  params: TreeSelectorParams;
  defaultItems: TreeItem[];
  showAll = false;
  deletables = [];
  filteredItems: TreeItem[];
  isRender = false;
  showEditableTemplate = false;
  get itemsById(): Map<string, TreeItem> {
    return indexMetadataByID(this.params.items);
  }

  get itemsByLabel(): Map<string, TreeItem> {
    return indexMetadataByLabel(this.params.items);
  }

  emitChangeEvent(checked, id, item, parent) {
    this.emitEvent<TreeSelectorChangePayload>('change', {
      checked,
      id,
      item,
      parent,
      items: this.params.selected,
      isProduct: true,
    });
  }

  emitChangeTemplateEvent(checked, id, item, parent) {
    this.emitEvent<TreeSelectorChangePayload>('change', {
      checked,
      id,
      item,
      parent,
      items: [this.params.template],
      isTemplateFilter: true,
    });
  }

  handleChange = (event: Event) => {
    const {
      checked,
      dataset: { id },
    } = event.target as HTMLInputElement;
    event.stopPropagation();
    const item = this.itemsById.get(id);
    if (checked) {
      this.params.selected.push(item.i18nLabel);
      this.deletables = [...this.deletables, item.i18nLabel];
    } else {
      this.params.selected = this.params.selected.filter((p) => p !== item.i18nLabel);
      this.deletables = this.deletables.filter((p) => p !== item.i18nLabel);
    }
    this.handleExpressCondition(item, checked);
    this.filteredItems = [];
    this.emitChangeEvent(checked, id, item, null);
  };

  handleTemplateFilter = (event: Event) => {
    const {
      checked,
      dataset: { id },
    } = event.target as HTMLInputElement;
    if (!this.showEditableTemplate) {
      return;
    }
    event.stopPropagation();
    if (checked && id === INCLUDES_TEMPLATE) {
      this.params.template = INCLUDES_TEMPLATE;
    } else {
      this.params.template = SHOW_ALL;
    }
    this.requestUpdate();
    this.isRender = !this.isRender;
    this.emitChangeTemplateEvent(true, id, id, this);
  };

  handlePopOverClick = (event) => {
    const {
      dataset: { id },
    } = event.target as HTMLInputElement;
    const item = this.itemsById.get(id);
    this.deletables = [...this.deletables, item.i18nLabel];
    this.params.selected.push(item.i18nLabel);
    this.filteredItems = [];
    this.handleExpressCondition(item, true);
    this.emitChangeEvent(true, id, item, null);
  };

  handleSearchChange = (event: Event) => {
    const query = event.target as Textfield;
    if (isEmpty(query.value)) {
      this.filteredItems = [];
      return;
    }
    const formattedQuery = this.replaceLabel(query.value).toLowerCase();
    this.filteredItems = this.params.items.filter(
      (item) => item.i18nLabel.toLowerCase().includes(formattedQuery) && !this.isChecked(item)
    );
  };

  searchDebounced = debounce(this.handleSearchChange, DEBOUNCE_TIME);

  toggleShowAll = () => {
    this.showAll = !this.showAll;
  };

  isChecked = (item) => this.params.selected.indexOf(item.i18nLabel) > -1;

  isFilterChecked = (item) => this.params.template === item;

  replaceLabel = (label: string): string => label.replace('Adobe', '').trim();

  isDisabled = () => this.params.template === SHOW_ALL;

  handleExpressCondition = (item: TreeItem, checked: boolean) => {
    if (!this.showEditableTemplate) {
      return;
    }
    // Express product
    if (item.id === '9cb258a2-5b8c-4e6b-a197-c8f5cec7c938') {
      if (!checked) {
        this.params.template = SHOW_ALL;
      }
    }
  };

  isExpressSelected = () => this.params.selected.indexOf(ADOBE_EXPRESS) > -1;

  renderTree() {
    const { params } = this;
    return html`
      <ul role="tree" class="filter-tree ${classMap({ inline: params.inline })}" aria-labelledby=${params.label}>
        <sp-search
          @input=${this.searchDebounced}
          class="search-input"
          placeholder=${$t('i18n.product.search')}
          autocomplete="on"
        ></sp-search>
        <div>
          ${this.filteredItems?.length > 0
            ? html`
                <sp-popover class="tree-popover" open>
                  <sp-menu>
                    ${this.filteredItems.map(
                      (filteredItem) => html`
                        <sp-menu-item
                          data-id=${filteredItem.id}
                          @click=${this.handlePopOverClick}
                        >
                          ${this.replaceLabel(filteredItem.i18nLabel)}
                        </<sp-menu-item>
                      `
                    )}
                  </sp-menu>
                </sp-popover>
              `
            : ''}
          ${this.deletables?.length > 0
            ? html`
                <div class="tree-tags">
                  <sp-tags>
                    ${this.deletables.map(
                      (item) => html`
                        <sp-tag
                          deletable
                          @delete=${() => {
                            this.deletables = this.deletables.filter((deletable) => {
                              return item !== deletable;
                            });
                            const itemData = this.itemsByLabel.get(item);
                            this.params.selected = this.params.selected.filter((p) => p !== item);
                            this.handleExpressCondition(itemData, false);
                            this.emitChangeEvent(true, itemData.id, itemData, null);
                          }}
                        >
                          ${$t(this.replaceLabel(item))}
                        </sp-tag>
                      `
                    )}
                  </sp-tags>
                </div>
              `
            : ''}
          ${this.showAll
            ? html`
                <div class="tree-item-show-all">
                  ${this.params.items.map((item, index) => this.renderTreeItem({ ...params, item, index }))}
                </div>
                <div class="view-less">
                  <a class="view-products-less" href="javascript:void(0);" @click=${this.toggleShowAll}>
                    ${$t('i18n.product.view_less_products')}
                  </a>
                </div>
              `
            : html`
                <div class="tree-item">
                  <div class="tree-message">
                    <h5>${$t('i18n.product.frequently_viewed')}</h5>
                  </div>
                  <div class="tree-list">
                    ${this.defaultItems.map((item, index) => this.renderTreeItem({ ...params, item, index }))}
                  </div>
                </div>
                <div>
                  <a class="view-products-more" href="javascript:void(0);" @click=${this.toggleShowAll}>
                    ${$t('i18n.product.view_more_products')}
                  </a>
                </div>
              `}
        </div>
        ${this.isExpressSelected() && this.showEditableTemplate
          ? html`
          <div class="tree-item-include-template">
          <hr />
          <div class="tree-item-include-template-message">
            <h5>${$t('i18n.product.includes_editable_template')}</h5>
          </div>
          <div class="tree-item-include-template-items">${this.renderTemplateFilter()}</div>
        </div>
      </ul>
        `
          : ''}
      </ul>
    `;
  }

  renderTreeItem = (params: TreeItemParams) => {
    const { item, items, index } = params;
    const { id, i18nLabel } = item;
    const level = 1;
    return html`
      <li
        role="treeitem"
        aria-level=${1}
        aria-setsize=${items.length}
        aria-posinset=${index + 1}
        tabindex=${level === 1 && index === 0 ? 0 : -1}
        data-id=${id}
      >
        <span>
          <sp-checkbox data-id=${id} ?checked=${this.isChecked(item)} @change=${this.handleChange}>
            ${$t(this.replaceLabel(i18nLabel))}
          </sp-checkbox>
        </span>
      </li>
    `;
  };

  renderTemplateFilter = () => {
    return html`
      <li role="treeitem" aria-level=${1} aria-setsize=${2}>
        <span>
          <sp-checkbox
            data-id=${SHOW_ALL}
            ?checked=${this.isFilterChecked(SHOW_ALL)}
            ?readonly=${this.isDisabled()}
            @change=${this.handleTemplateFilter}
            class=${classMap({
              'unselectable-checkbox': this.isDisabled(),
            })}
          >
            ${$t('Show All')}
          </sp-checkbox>
          <sp-checkbox
            data-id=${INCLUDES_TEMPLATE}
            ?checked=${this.isFilterChecked(INCLUDES_TEMPLATE)}
            @change=${this.handleTemplateFilter}
          >
            ${$t('Includes Template')}
          </sp-checkbox>
        </span>
      </li>
    `;
  };

  render() {
    return this.renderTree();
  }
}
